// Shared post-login pipeline.
//
// Three call-sites land here: SSO completion (`completeLogin`),
// restored sessions (`tryRestoreSession` in the boot effect), and the
// embed surface (`preAuthedClient` in the boot effect). Keeping the
// pipeline in one module is the structural payoff of #1311 — the
// branch lives upstream (which credentials produced the client), the
// downstream sequence (crypto → sync → subscriptions → identity) is
// identical, and divergence is impossible by construction.
//
// The function is deliberately framework-agnostic: it takes the React
// state setters and refs as plain callbacks / mutable wrappers. That
// makes it directly testable from `vitest` against the stub bindings
// without booting React.

import {
  bootstrapCrossSigning,
  ensureKeyBackup,
  ensureSecretStorage,
  finishOidcLogin,
  getDisplayName,
  getUserId,
  hasKeyBackup,
  hasSecretStorageKey,
  initCrypto,
  isBackupExistsError,
  isStaleCryptoStoreError,
  restoreKeyBackupWithCachedKey,
  startSync,
  subscribeRooms,
  subscribeSavedItems,
  type Client,
  type ClientState,
  type IAuthData,
  type InteractiveAuthCallback,
  type RoomSummary,
  type SavedItem,
} from "~/lib/matrix";
import { markBoot } from "~/lib/boot-timing";

/** Mutable single-slot wrapper. Mirrors React's `MutableRefObject` so
 *  the same object passed to a hook also works with the pipeline. */
export interface MutableRef<T> {
  current: T;
}

/**
 * Tear down and null out a subscription in one place so every call
 * site (boot effect cleanup, completeLogin re-entry, signOut) handles
 * leaks identically. Two writers to the same ref without this helper
 * is how the double-subscription bug reproduces.
 *
 * Use `teardownSub` when you intentionally own the slot — e.g. you
 * are about to install your own subscription, or the caller (signOut,
 * unmount cleanup) deliberately wants to release whatever is there.
 * Use `teardownSubIfOwner` when a stale pipeline is bailing on
 * cancellation and must NOT release a successor pipeline's
 * subscription.
 */
export function teardownSub(ref: MutableRef<(() => void) | null>): void {
  const unsub = ref.current;
  if (unsub) {
    unsub();
    ref.current = null;
  }
}

/**
 * Owner-aware variant of `teardownSub`. Only nulls and tears down
 * the slot if it still holds the exact unsub the caller installed.
 *
 * Why this matters: when a superseded pipeline (older generation)
 * detects cancellation, the shared subscription refs may have
 * already been swapped to a *successor* pipeline's subscriptions.
 * Calling plain `teardownSub` from the bail path of the older
 * pipeline would tear down the newer pipeline's live subs — exactly
 * the leak the generation guard was meant to prevent. Comparing
 * `ref.current === ownUnsub` makes the cleanup obviously safe by
 * inspection: a stale pipeline only releases what it owns.
 */
export function teardownSubIfOwner(
  ref: MutableRef<(() => void) | null>,
  ownUnsub: (() => void) | null,
): void {
  if (ownUnsub && ref.current === ownUnsub) {
    ownUnsub();
    ref.current = null;
  }
}

/**
 * Default interactive-auth handler used by the crypto bootstrap.
 *
 * The stub never requires UIA. With MAS the homeserver replaces UIA
 * with OAuth-flavoured re-auth (`prompt=login` against `oauth()`); the
 * `bootstrapCrossSigning` path can still surface a Synapse-style UIA
 * payload during the migration window, so the warn log keeps the
 * silent-failure mode observable until the real handler lands. Wiring
 * the OAuth re-auth flow is out of scope for #3156 and tracked as a
 * follow-up (chat-mas/2 cleanup; UIA replacement is #3156+1).
 *
 * TODO(#3156+1): replace with a real OAuth re-auth flow that opens
 * `oauth().login(...)` with `additional_scopes` set to mark the
 * request as a re-auth, or surfaces a password prompt dialog when
 * the homeserver still expects classic UIA.
 */
export const defaultInteractiveAuth = async (req: IAuthData): Promise<IAuthData> => {
  if (req.flows && req.flows.length > 0) {
    console.warn(
      "defaultInteractiveAuth: homeserver requested UIA, but no handler is wired yet. Flows:",
      req.flows,
    );
  }
  return req;
};

export interface BootstrapCryptoCallbacks {
  setCryptoReady: (v: boolean) => void;
  setNeedsRecovery: (v: boolean) => void;
  setPendingRecoveryKey: (v: string | null) => void;
  setCryptoError: (stage: CryptoBootstrapStage, err: unknown) => void;
  skipRecoveryGate?: () => boolean;
}

export interface BootstrapCryptoOptions {
  interactiveAuth?: InteractiveAuthCallback;
  skipRecoveryGate?: boolean;
  beforeRecoveryCheck?: () => Promise<void>;
}

export type CryptoBootstrapStage = "initCrypto" | "bootstrapCrossSigning" | "ensureKeyBackup";

export interface CryptoBootstrapError {
  stage: CryptoBootstrapStage;
  message: string;
}

export function toCryptoBootstrapError(
  stage: CryptoBootstrapStage,
  err: unknown,
): CryptoBootstrapError {
  return {
    stage,
    message: err instanceof Error ? err.message : "Encryption setup failed",
  };
}

/**
 * Shared crypto-bootstrap path. Called on every session entry
 * (restored session + fresh login) so the order is identical:
 *
 *   initCrypto → bootstrapCrossSigning → (branch on server state)
 *
 * After cross-signing, we decide the downstream flow by querying
 * the server, not by trusting the caller:
 *
 *   - no backup, no local key           → ensureSecretStorage +
 *                                         ensureKeyBackup, show
 *                                         recovery key once
 *                                         (account-first-device)
 *   - backup exists, local key missing  → prompt for recovery key
 *                                         (new-device flow)
 *   - backup exists, local key present  → download missing backup keys,
 *                                         then idle
 *   - no backup, local key present      → ensureKeyBackup to
 *                                         re-publish (interrupted
 *                                         first-login or server-
 *                                         side backup deletion)
 *
 * Idempotency is carried by the underlying `ensure*` helpers — a
 * device that already has everything lands in a silent branch.
 *
 * Sets `cryptoReady` true once `initCrypto` resolves so UI gating
 * can drop its loading branch; the remaining steps run in the
 * background.
 *
 * Errors are surfaced as a non-fatal error state — encrypted rooms
 * can show recovery UI while unencrypted rooms remain usable.
 *
 * Cancellation is checked between every awaited boundary so an
 * unmount or supersede mid-flight doesn't write into stale React
 * state. The early-out is silent — the caller's own cancellation
 * path is responsible for any teardown.
 */
export async function bootstrapCrypto(
  client: Client,
  cb: BootstrapCryptoCallbacks,
  isCancelled: () => boolean = () => false,
  options: BootstrapCryptoOptions = {},
): Promise<void> {
  const runCrossSigning = async (): Promise<boolean> => {
    try {
      await bootstrapCrossSigning(client, options.interactiveAuth ?? defaultInteractiveAuth);
      if (isCancelled()) return false;
      return true;
    } catch (err) {
      if (isCancelled()) return false;
      if (isStaleCryptoStoreError(err)) throw err;
      cb.setCryptoError("bootstrapCrossSigning", err);
      console.warn("bootstrapCrossSigning failed", err);
      return false;
    }
  };

  markBoot("crypto-init-start");
  try {
    await initCrypto(client);
    if (isCancelled()) return;
    markBoot("crypto-init-done");
    cb.setCryptoReady(true);
  } catch (err) {
    if (isCancelled()) return;
    if (isStaleCryptoStoreError(err)) throw err;
    cb.setCryptoError("initCrypto", err);
    console.error("initCrypto failed", err);
    return;
  }
  if (isCancelled()) return;
  if (options.beforeRecoveryCheck) {
    await options.beforeRecoveryCheck();
    if (isCancelled()) return;
  }
  try {
    const [backup, localKey] = await Promise.all([
      hasKeyBackup(client),
      hasSecretStorageKey(client),
    ]);
    if (isCancelled()) return;
    if (backup) {
      if (!localKey) {
        cb.setNeedsRecovery(true);
        if (options.skipRecoveryGate && !(await runCrossSigning())) return;
        return;
      }
      if (!(await runCrossSigning())) return;
      void restoreKeyBackupWithCachedKey(client).catch((err) => {
        if (isCancelled()) return;
        cb.setCryptoError("ensureKeyBackup", err);
        console.warn("key backup bootstrap failed", err);
      });
      return;
    }
    if (!(await runCrossSigning())) return;
    if (!localKey) {
      const { recoveryKey } = await ensureSecretStorage(client);
      if (isCancelled()) return;
      if (recoveryKey) cb.setPendingRecoveryKey(recoveryKey);
    }
    await ensureKeyBackup(client);
  } catch (err) {
    if (isCancelled()) return;
    if (isStaleCryptoStoreError(err)) throw err;
    if (isBackupExistsError(err)) {
      cb.setPendingRecoveryKey(null);
      cb.setNeedsRecovery(true);
      return;
    }
    cb.setCryptoError("ensureKeyBackup", err);
    console.warn("key backup bootstrap failed", err);
  }
}

export interface PostLoginPipelineOptions extends BootstrapCryptoCallbacks {
  /** Cancellation flag. Returns true when the pipeline must stop
   *  writing into React state — caller's React unmount cleanup, a
   *  superseding pipeline run (e.g. a re-login while a restore is
   *  mid-flight), or any race the caller wants to debounce. The
   *  pipeline re-checks after every awaited boundary, including
   *  between every internal step of `bootstrapCrypto` and between
   *  the room/saved subscription installs. */
  isCancelled: () => boolean;
  interactiveAuth: InteractiveAuthCallback;
  /** Sync state callback. The component pipes this through React
   *  `setState`; tests inject an array push to assert state ordering. */
  onState: (state: ClientState) => void;
  setRooms: (rooms: RoomSummary[]) => void;
  setSavedItems: (items: SavedItem[]) => void;
  setDisplayName: (name: string | null) => void;
  setUserId: (id: string | null) => void;
  /** Single-active-pipeline guard. Prevents StrictMode double-invoke
   *  or HMR re-mount from running the pipeline twice for the SAME
   *  React mount. Deliberate re-logins (signOut → SSO callback)
   *  must reset this BEFORE calling — see `runOidcLogin`.
   *
   *  Lifetime: set to `true` on entry, never cleared on the happy
   *  path. Cleared explicitly by:
   *    - the caller's signOut handler (so a same-tab re-login can
   *      re-run the pipeline),
   *    - the caller's unmount cleanup (so a re-mount under HMR
   *      doesn't see a stale armed guard),
   *    - `runOidcLogin` before delegating, so a deliberate re-login
   *      via OIDC restarts the pipeline end-to-end.
   *  This is the same shape the inline pipeline used pre-#2377; the
   *  guard is about debouncing duplicate invocations within a
   *  single mount, not about reflecting "currently running". */
  syncActiveRef: MutableRef<boolean>;
  roomsUnsubRef: MutableRef<(() => void) | null>;
  savedUnsubRef: MutableRef<(() => void) | null>;
}

/**
 * Drive the post-login pipeline (subscriptions + identity → crypto →
 * sync) for any path that ends with an authenticated client: a
 * session restored from local storage, a fresh SSO completion, or a
 * host-supplied pre-authed embed client. Centralising the pipeline
 * keeps every entry point behaviourally identical from the consumer's
 * perspective and removes the class of bugs where a fix to one path
 * is forgotten on the other.
 *
 * The subscriptions (rooms + saved items) and the user id are
 * installed BEFORE `bootstrapCrypto`, not after it: the room snapshot
 * and identity are available synchronously from the client, while
 * crypto bootstrap is a multi-round-trip (and possibly interactive)
 * sequence. Installing them first means the shell's gate can keep
 * holding the syncing screen until the room snapshot lands instead of
 * flashing an empty sidebar the moment `bootstrapCrypto` yields.
 *
 * `getDisplayName` is fire-and-forget so a slow display-name lookup can
 * never hold the sync / subscription path. The user id is read
 * synchronously and set before any `await`.
 *
 * Cancellation is checked after every awaited boundary so an unmount
 * or org-switch doesn't strand a setState into a torn-down tree. The
 * `bail` helper tears down only this pipeline's own subscriptions —
 * a superseded pipeline must NOT release a successor pipeline's.
 */
export async function runPostLoginPipeline(
  client: Client,
  opts: PostLoginPipelineOptions,
): Promise<void> {
  if (opts.syncActiveRef.current) return;
  opts.syncActiveRef.current = true;

  opts.setUserId(getUserId(client));

  // Install both subscriptions before `bootstrapCrypto` so the rooms
  // list lands while recovery runs instead of flashing an empty
  // sidebar. Each unsub is torn down only by its own owner.
  teardownSub(opts.roomsUnsubRef);
  const roomsUnsub = subscribeRooms(client, (list) => {
    if (opts.isCancelled()) return;
    if (list.length > 0) markBoot("rooms-first");
    opts.setRooms(list);
  });
  opts.roomsUnsubRef.current = roomsUnsub;
  teardownSub(opts.savedUnsubRef);
  const savedUnsub = subscribeSavedItems(client, (items) => {
    if (opts.isCancelled()) return;
    opts.setSavedItems(items);
  });
  opts.savedUnsubRef.current = savedUnsub;
  const bail = () => {
    teardownSubIfOwner(opts.roomsUnsubRef, roomsUnsub);
    teardownSubIfOwner(opts.savedUnsubRef, savedUnsub);
  };
  if (opts.isCancelled()) return bail();

  // Fire-and-forget; the `.catch` handles CLIENT_INIT_CANCELLED on
  // logout, which the old awaited path routed to boot()'s catch.
  void getDisplayName(client)
    .then((name) => {
      if (!opts.isCancelled()) opts.setDisplayName(name);
    })
    .catch(() => undefined);

  let syncStarted = false;
  await bootstrapCrypto(client, opts, opts.isCancelled, {
    interactiveAuth: opts.interactiveAuth,
    skipRecoveryGate: opts.skipRecoveryGate?.() ?? false,
    beforeRecoveryCheck: async () => {
      syncStarted = true;
      markBoot("sync-start");
      await startSync(client, opts.onState);
    },
  });
  if (opts.isCancelled()) return bail();
  if (!syncStarted) {
    markBoot("sync-start");
    await startSync(client, opts.onState);
    if (opts.isCancelled()) return bail();
  }
}

/**
 * OIDC entry point. Exchanges the MAS callback query string for an
 * access token via `finishOidcLogin`, then drives the shared
 * post-login pipeline.
 *
 * Re-entrancy: this verb is fired by explicit user actions (the
 * web `/oidc/callback` handler, the Tauri deeplink listener), so a
 * second call is intentional — a re-login race or a logout/login
 * round-trip — and must restart the pipeline. The pipeline's
 * `syncActiveRef` debounces StrictMode double-invokes within one
 * React mount; we reset it here so a deliberate re-login re-runs
 * end-to-end. The underlying `teardownSub` helpers ensure the
 * previous subscription is released before the new one lands.
 *
 * Cancellation: callers pass `opts.isCancelled` so an unmount or a
 * superseding pipeline run can short-circuit any awaited boundary
 * — same contract as `runPostLoginPipeline`. Errors during token
 * exchange or pipeline execution are surfaced via `onError` so the
 * caller can flip its sync state to `"error"` without this module
 * depending on a `ClientState` enum at the OIDC boundary.
 */
export async function runOidcLogin(
  client: Client,
  callbackQuery: string,
  opts: PostLoginPipelineOptions & { onError: (err: unknown) => void | Promise<void> },
): Promise<void> {
  try {
    await finishOidcLogin(client, callbackQuery);
    if (opts.isCancelled()) return;
    opts.syncActiveRef.current = false;
    await runPostLoginPipeline(client, opts);
  } catch (err) {
    console.error("Matrix login failed", err);
    await opts.onError(err);
  }
}
